import React, { useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

const TwoFactorStep = ({
	challenge,
	startEnrollment,
	verifyTwoFactor,
	confirmTwoFactorEnrollment,
	onSuccess,
	onBack,
}) => {
	const challengeToken = challenge && challenge.challengeToken
	const isEnrollment = Boolean(challenge && challenge.requiresEnrollment)
	const [setup, setSetup] = useState(null)
	const [code, setCode] = useState('')
	const [error, setError] = useState('')
	const [isLoading, setIsLoading] = useState(false)

	useEffect(() => {
		setSetup(null)
		setCode('')
		setError('')
	}, [challengeToken])

	useEffect(() => {
		let isMounted = true

		if (!isEnrollment || !challengeToken) {
			return () => {
				isMounted = false
			}
		}

		setIsLoading(true)
		startEnrollment(challengeToken)
			.then(response => {
				if (isMounted && response) {
					setSetup(response)
				}
			})
			.catch(() => {
				if (isMounted) {
					setError('ไม่สามารถเตรียมการลงทะเบียน 2FA ได้ กรุณาเริ่มเข้าสู่ระบบใหม่')
				}
			})
			.finally(() => {
				if (isMounted) {
					setIsLoading(false)
				}
			})

		return () => {
			isMounted = false
		}
	}, [challengeToken, isEnrollment, startEnrollment])

	const onCodeChange = event => {
		setError('')
		setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
	}

	const onSubmit = async event => {
		event.preventDefault()
		if (code.length !== 6) {
			setError('กรุณากรอกรหัส 6 หลักจากแอป Authenticator')
			return
		}

		setIsLoading(true)
		setError('')
		try {
			const response = isEnrollment
				? await confirmTwoFactorEnrollment({ challengeToken, code })
				: await verifyTwoFactor({ challengeToken, code })

			if (!response) {
				setError('รหัส 2FA ไม่ถูกต้องหรือหมดอายุแล้ว')
				return
			}

			await onSuccess(response)
		} catch (requestError) {
			setError(
				requestError?.response?.data?.message ||
				'รหัส 2FA ไม่ถูกต้องหรือหมดอายุแล้ว'
			)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Card className="flex flex-col items-center justify-center px-6 py-6 w-full">
			<Grid container direction="column" spacing={2}>
				<Grid item>
					<Typography variant="h6" align="center">
						{isEnrollment ? 'ตั้งค่า 2FA' : 'ยืนยันตัวตน 2FA'}
					</Typography>
					<Typography variant="body2" align="center">
						{isEnrollment
							? 'สแกน QR Code ด้วย Google Authenticator หรือ Microsoft Authenticator'
							: 'เปิดแอป Authenticator แล้วกรอกรหัส 6 หลักล่าสุด'}
					</Typography>
				</Grid>

				{isEnrollment && isLoading && !setup && (
					<Grid item container justifyContent="center">
						<CircularProgress size={28} />
					</Grid>
				)}

				{isEnrollment && setup && (
					<>
						<Grid item container justifyContent="center">
							<img
								alt="QR Code สำหรับลงทะเบียน 2FA"
								src={setup.qrCodeDataUri}
								style={{ width: 220, height: 220 }}
							/>
						</Grid>
						<Grid item>
							<TextField
								fullWidth
								label="คีย์สำหรับกรอกด้วยตนเอง"
								value={setup.manualEntryKey || ''}
								InputProps={{ readOnly: true }}
							/>
						</Grid>
					</>
				)}

				<Grid item>
					<Typography variant="body2">
						{isEnrollment
							? 'หลังเพิ่มบัญชีแล้ว ให้กรอกรหัสที่แสดงในแอปเพื่อยืนยัน'
							: 'รหัสมีอายุสั้น กรุณาใช้รหัสล่าสุดจากแอป'}
					</Typography>
				</Grid>

				<Grid item>
					<form onSubmit={onSubmit}>
						<TextField
							fullWidth
							label="รหัส 2FA 6 หลัก"
							value={code}
							onChange={onCodeChange}
							autoFocus={!isEnrollment}
							inputProps={{
								inputMode: 'numeric',
								pattern: '[0-9]*',
								maxLength: 6,
							}}
							error={Boolean(error)}
							helperText={error || ' '}
						/>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<Button
									type="submit"
									fullWidth
									variant="contained"
									color="secondary"
									disabled={isLoading || code.length !== 6}
								>
									{isLoading ? 'กำลังตรวจสอบ...' : 'ยืนยัน 2FA'}
								</Button>
							</Grid>
							<Grid item xs={12}>
								<Button
									fullWidth
									variant="text"
									disabled={isLoading}
									onClick={onBack}
								>
									กลับไปหน้าเข้าสู่ระบบ
								</Button>
							</Grid>
						</Grid>
					</form>
				</Grid>
				<Divider />
				<Grid item>
					<Typography variant="caption" color="textSecondary">
						หากลงทะเบียนไม่ทันภายในเวลาที่กำหนด ให้กลับไปเข้าสู่ระบบใหม่
					</Typography>
				</Grid>
			</Grid>
		</Card>
	)
}

export default TwoFactorStep
